import React, { Component } from 'react'
import PropTypes from 'prop-types'

function hasGetUserMedia() {
  return Boolean(
    navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia
  )
}

export default class Webcam extends Component {
  static defaultProps = {
    audio: true,
    className: '',
    height: 480,
    muted: false,
    onUserMedia: () => {},
    screenshotFormat: 'image/webp',
    width: 640
  }

  static propTypes = {
    audio: PropTypes.bool,
    muted: PropTypes.bool,
    onUserMedia: PropTypes.func,
    getVideoSources: PropTypes.func,
    getAudioSources: PropTypes.func,
    onGetUserMedia: PropTypes.func,
    fallback: PropTypes.func,
    onVideoRecordStart: PropTypes.func,
    onDataAvailable: PropTypes.func,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    screenshotFormat: PropTypes.oneOf(['image/webp', 'image/png', 'image/jpeg']),
    style: PropTypes.object,
    className: PropTypes.string,
    audioSource: PropTypes.string,
    videoSource: PropTypes.string
  }

  static mountedInstances = []

  static userMediaRequested = false

  constructor() {
    super()
    this.state = {
      hasUserMedia: false
    }
    this.recordedBlobs = []
  }

  componentDidMount() {
    try {
      if (!hasGetUserMedia()) return

      Webcam.mountedInstances.push(this)

      if (!this.state.hasUserMedia && !Webcam.userMediaRequested) {
        this.requestUserMedia()
      }
    } catch (error) {
      console.log(`${error.name}: ${error.message}`) // eslint-disable-line no-console
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.videoSource != this.props.videoSource) this.requestUserMedia()
  }

  componentWillUnmount() {
    try {
      const index = Webcam.mountedInstances.indexOf(this)
      Webcam.mountedInstances.splice(index, 1)

      if (0 === Webcam.mountedInstances.length && this.state.hasUserMedia) {
        if (this.stream.stop) {
          this.stream.stop()
        } else {
          if (this.stream.getVideoTracks) {
            this.stream.getVideoTracks().map(track => track.stop())
          }
          if (this.stream.getAudioTracks) {
            this.stream.getAudioTracks().map(track => track.stop())
          }
        }
        Webcam.userMediaRequested = false
      }
    } catch (error) {
      console.log(`${error.name}: ${error.message}`) // eslint-disable-line no-console
    }
  }

  getScreenshot() {
    if (!this.state.hasUserMedia) return null

    const canvas = this.getCanvas()
    return canvas.toDataURL(this.props.screenshotFormat)
  }

  startRecordVideo() {
    const self = this
    this.recordedBlobs = []
    try {
      const options = (() => {
        if (!MediaRecorder.isTypeSupported('video/webm')) {
          console.error(options.mimeType + ' is not Supported') // eslint-disable-line no-console
          if (!MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
            console.error('video/webm;codecs=vp8  is not Supported') // eslint-disable-line no-console
            if (!MediaRecorder.isTypeSupported('video/webm')) {
              console.error('video/webm is not Supported') // eslint-disable-line no-console
              return { mimeType: '' }
            }
            return { mimeType: 'video/webm' }
          }
          return { mimeType: 'video/webm;codecs=vp8' }
        }
        return { mimeType: 'video/webm' }
      })()

      try {
        this.mediaRecorder = new MediaRecorder(this.stream, options)
      } catch (error) {
        console.error('Exception while creating MediaRecorder: ' + error) // eslint-disable-line no-console
        if ('function' == typeof this.props.fallback) this.props.fallback()
        return
      }
      if ('function' == typeof this.props.onDataAvailable)
        this.mediaRecorder.addEventListener('dataavailable', function (event) {
          if (event.data && event.data.size > 0) {
            self.props.onDataAvailable(event)
            self.recordedBlobs.push(event.data)
          }
        })

      this.mediaRecorder.start(10)
      if ('function' == typeof this.props.onVideoRecordStart) this.props.onVideoRecordStart()
    } catch (error) {
      console.error('Exception with MediaRecorder: ' + error) // eslint-disable-line no-console
      if ('function' == typeof this.props.fallback) this.props.fallback()
    }
  }

  stopRecordVideo() {
    if (this.mediaRecorder) this.mediaRecorder.stop()
    return this.recordedBlobs
  }

  getCanvas() {
    if (!this.state.hasUserMedia) return null

    const video = this.refVideo

    if (!this.ctx || (this.canvas && !this.canvas.height)) {
      const canvas = document.createElement('canvas')
      const aspectRatio = video.videoWidth / video.videoHeight

      canvas.width = video.clientWidth
      canvas.height = video.clientWidth / aspectRatio

      this.canvas = canvas
      this.ctx = canvas.getContext('2d')
    }

    const { ctx, canvas } = this
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    return canvas
  }

  requestUserMedia() {
    const isSecureOrigin = 'https:' === location.protocol || 'localhost' === location.hostname
    const self = this
    if (!isSecureOrigin) {
      return
    }

    navigator.getUserMedia =
      navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia
    if (null == navigator.getUserMedia) {
      return // no getUserMedia found event with prefixes
    }

    const sourceSelected = (audioSource, videoSource) => {
      const constraints = {
        video: {
          optional: [{ sourceId: videoSource }]
        }
      }

      if (this.props.audio) {
        constraints.audio = {
          optional: [{ sourceId: audioSource }]
        }
      }

      navigator.getUserMedia(
        constraints,
        stream => {
          Webcam.mountedInstances.forEach(instance => instance.handleUserMedia(null, stream))
          self.stream = stream
          if ('function' == typeof self.props.onGetUserMedia) self.props.onGetUserMedia(stream)
        },
        event => {
          Webcam.mountedInstances.forEach(instance => instance.handleUserMedia(event))
        }
      )
    }

    if (this.props.audioSource && this.props.videoSource) {
      sourceSelected(this.props.audioSource, this.props.videoSource)
    } else if (null != navigator.mediaDevices) {
      navigator.mediaDevices
        .enumerateDevices()
        .then(devices => {
          let audioSource = null
          let videoSource = null

          if ('function' == typeof this.props.getVideoSources)
            this.props.getVideoSources(devices.filter(d => 'video' == d.kind || 'videoinput' == d.kind))

          if ('function' == typeof this.props.getAudioSources)
            this.props.getAudioSources(devices.filter(d => 'audioinput' == d.kind))

          devices.forEach(device => {
            if ('audio' === device.kind) {
              audioSource = device.id
            } else if ('video' === device.kind) {
              videoSource = device.id
            }
          })

          if (this.props.videoSource) videoSource = this.props.videoSource

          sourceSelected(audioSource, videoSource)
        })
        .catch(error => {
          console.log(`${error.name}: ${error.message}`) // eslint-disable-line no-console
        })
    } else {
      MediaStreamTrack.getSources(sources => {
        let audioSource = null
        let videoSource = null

        sources.forEach(source => {
          if ('audio' === source.kind) {
            audioSource = source.id
          } else if ('video' === source.kind) {
            videoSource = source.id
          }
        })

        sourceSelected(audioSource, videoSource)
      })
    }

    Webcam.userMediaRequested = true
  }

  handleUserMedia(error, stream) {
    if (error) {
      this.setState({
        hasUserMedia: false
      })

      return
    }

    this.stream = stream
    this.setState({
      hasUserMedia: true
    })
    this.refVideo.srcObject = stream
    this.props.onUserMedia()
  }

  render() {
    if (!this.state.hasUserMedia) this.requestUserMedia()
    return (
      <video
        autoPlay
        width={this.props.width}
        height={this.props.height}
        muted={this.props.muted}
        className={this.props.className}
        style={this.props.style}
        data-testid="SW-VR-Webcam"
        ref={el => {
          this.refVideo = el
        }}
      />
    )
  }
}
